import type { Filter, PersistedState } from './types'

const OP_TO_SHORT: Record<string, string> = {
  equals: 'eq',
  not_equals: 'ne',
  greater_than: 'gt',
  greater_than_or_equal: 'gte',
  less_than: 'lt',
  less_than_or_equal: 'lte',
  between: 'btw',
  contains: 'has',
  not_contains: 'nothas',
  starts_with: 'sw',
  ends_with: 'ew',
  in: 'in',
  not_in: 'notin',
  before: 'before',
  after: 'after',
}

const SHORT_TO_OP: Record<string, Filter['operator']> = Object.fromEntries(
  Object.entries(OP_TO_SHORT).map(([k, v]) => [v, k as Filter['operator']]),
)

const opPattern = Object.values(OP_TO_SHORT).join('|')
const paramRegex = new RegExp(`^(.+)_(${opPattern})$`)

const parseValue = (v: string) => {
  if (v === 'null') return null
  if (v === 'undefined') return undefined
  if (v.startsWith('[')) {
    try {
      return JSON.parse(v)
    } catch {}
  }
  const num = Number(v)
  if (!Number.isNaN(num) && v !== '') return num
  return v
}

const parseFilterParam = (key: string, value: string): Filter | null => {
  const match = key.match(paramRegex)
  if (!match) return null

  const [, field, shortOp] = match
  const operator = SHORT_TO_OP[shortOp]
  if (!operator) return null

  const parts = value.startsWith('[') ? [value] : value.split('_')
  return {
    id: crypto.randomUUID(),
    field,
    operator,
    value: parseValue(parts[0]),
    ...(parts[1] !== undefined && { value2: parseValue(parts[1]) }),
  }
}

export const getParams = (searchParams: URLSearchParams): string[] =>
  Array.from(searchParams.keys()).filter((key) => paramRegex.test(key))

export const fromParams = (
  searchParams: URLSearchParams,
): Partial<PersistedState> | null => {
  const filters = Array.from(searchParams.entries())
    .map(([key, value]) => parseFilterParam(key, value))
    .filter((f): f is Filter => f !== null)

  return filters.length > 0 ? { filters, dateRanges: {} } : null
}

export const toParams = (
  state: PersistedState,
): Record<string, string> | null => {
  if (state.filters.length === 0) return null

  return state.filters.reduce(
    (params: Record<string, string>, filter: Filter) => {
      const shortOp = OP_TO_SHORT[filter.operator]
      if (!shortOp) return params

      const key = `${filter.field}_${shortOp}`
      let value = Array.isArray(filter.value)
        ? JSON.stringify(filter.value)
        : filter.value === null
          ? 'null'
          : String(filter.value ?? '')
      if (filter.value2 !== undefined) {
        value += `_${filter.value2 === null ? 'null' : String(filter.value2)}`
      }
      params[key] = value
      return params
    },
    {},
  )
}
